import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { fetchSiteLanguages, siteDefaultLanguage } from '../../ad-language-api';
import { adBrandFrom, kitCopyFromSubject } from '../../ad-subjects';
import { newestLockedVersion, newestVersion, withCreated } from '../../ad-versions';
import { adTabRoutes, type AdView } from '../../asset-routes';
import {
  DEFAULT_KIT_MODEL,
  enabledKitSizes,
  exportBannerKit,
  fetchBannerKitCatalog,
  upsertBannerKit,
  type AdBrand,
  type AdSubject,
  type AdSubjectType,
  type BannerKit,
  type BannerKitCatalog,
  type BannerKitParams,
  type BannerKitVersion,
} from '../../banner-kit-api';
import type { CreativeItem } from '../../creative-api';
import { applyBrandTheme, fetchBrandTheme } from '../../brand-theme-api';
import { assetFilename, downloadAsset } from '../../download-asset';
import { revealOnLoad } from '../../reveal-media';
import { useAssetLibraryStats } from '../../useAssetLibraryStats';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { useBannerKitLibrary } from '../../useBannerKitLibrary';
import { AdLibraryShell, type AdCardState } from '../AdLibraryShell';
import { AdSubjectFlow } from '../AdSubjectFlow';
import { AssetCardStats, AssetCardStatsSkeleton, AssetStatsSheet } from '../AssetStats';
import { BannerKitComposer } from '../BannerKitComposer';
import { LumioMark } from '../LumioMark';
import * as S from './BannerKitLibrary.styles';

export interface BannerKitLibraryProps {
  tenantId?: number;
  brandName: string;
  brandDomain?: string;
  brandLogoUrl?: string;
  /** The brand's logo variants by slot (see AdBrand.logos). */
  brandLogos?: Record<string, string>;
  /** The brand's promotion pool (the subjects a kit can advertise, and the tracking link's `cta_href` source). */
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** The deep-linked view (library grid, create new, or a kit by id), derived from the URL by the page. */
  view: AdView;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

/** A new kit's document: the subject's own copy (locked, see kitCopyFromSubject; a Generate writes it in the kit's
 *  language), the Social kit, the UK/EU legal line (its text filled from the preset at export, in the kit's language),
 *  the catalogue's model and the website's default language ('' when it could not be read: the composer fills it
 *  later). Pure. */
const newKitParams = (
  subjectType: AdSubjectType,
  subject: AdSubject,
  brand: AdBrand,
  model: string,
  language: string,
): BannerKitParams => ({
  subject_type: subjectType,
  subject,
  copy: kitCopyFromSubject(subjectType, subject),
  brand,
  kits: ['social'],
  sizes_off: [],
  art_direction: '',
  legal: { enabled: true, preset: 'uk_eu', text: '' },
  model,
  language,
});

/** How long a create waits for the website's languages before the kit starts with none (the composer fills the
 *  default in once they are read). A network bound, not an animation timing. */
const LANGUAGE_WAIT_MS = 4000;

/** The website's default language, or '' when it is unknown or not read within LANGUAGE_WAIT_MS. Never rejects. */
const defaultLanguageWithin = (tenantId: number): Promise<string> =>
  Promise.race([
    siteDefaultLanguage(tenantId),
    new Promise<string>((resolve) => {
      window.setTimeout(() => resolve(''), LANGUAGE_WAIT_MS);
    }),
  ]);

/** The card poster: the square render of the newest locked version, else of the newest version with one, carrying
 *  the brand's real logo (the hero is drawn logo-free: every size is redrawn from it, and the logo is placed on the
 *  sizes in code), falling back to the hero for kits with no square size; '' when nothing has rendered. Never the
 *  subject key art the draft was minted with, which would pass a not-yet-generated kit off as a finished one. Pure. */
const SQUARE_SHAPE = '1';
const posterOf = (v: BannerKitVersion | undefined): string => v?.shapes?.[SQUARE_SHAPE]?.url || v?.hero?.url || '';
export const kitPosterUrl = (kit: BannerKit): string => {
  const withArt = kit.ad_versions.filter((v) => Boolean(posterOf(v)));
  return posterOf(newestLockedVersion(withArt) ?? newestVersion(withArt));
};

const isActive = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** A kit's card state: a run in flight, a generated (locked) version, or a draft. Pure. */
const kitCardState = (kit: BannerKit, jobsById: Record<string, CreativeJob>): AdCardState => {
  if (isActive(kit.job_id ? jobsById[kit.job_id] : undefined)) return 'generating';
  return newestLockedVersion(kit.ad_versions) ? 'ready' : 'draft';
};

/**
 * Marketing Assets > Image Banners: the brand's banner kits on the shared ad library shell (create tile, cards with the hero
 * as poster, Edit / Download ZIP / Archive, archived sheet). Creating one NAVIGATES to `/banners/new`, which runs
 * the subject flow (promotion, game or gamification, one item) and mints the draft on confirm, then swaps the URL
 * to the kit's id in place; opening one navigates to `/banners/:id`. The URL stays the single source of truth, so
 * browser back/forward and reloads work.
 */
export const BannerKitLibrary = ({
  tenantId,
  brandName,
  brandDomain,
  brandLogoUrl,
  brandLogos,
  items,
  itemsLoaded,
  view,
  onBuyCredits,
  onNotify,
}: BannerKitLibraryProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routes = adTabRoutes('banner-kits');
  const {
    items: kits,
    loading,
    loaded,
    settled,
    listFailed,
    refresh,
    remove,
    restore,
    purge,
    patch,
  } = useBannerKitLibrary(tenantId);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const viewId = view.kind === 'edit' ? view.id : undefined;

  // Each card's tracking-link figures (one grouped read for the grid; an entry iff the banner has a link), re-read
  // when a composer closes; a card's figures open the banner's stats sheet.
  const liveIds = React.useMemo(() => kits.filter((k) => !k.archived).map((k) => k.id), [kits]);
  const stats = useAssetLibraryStats('kit', liveIds, {
    onGrid: view.kind === 'library',
    idsLoaded: loaded && !listFailed,
  });
  // The stats sheet open over the grid is the address's (`?stats=<id>`), so Back from one of its deep links reopens it.
  // Read on the grid only: a composer open over it reads the same key for its own sheet.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsForId = view.kind === 'library' ? statsSheet.value : null;

  // The catalogue (sizes per kit, default model): static per deploy and cached by the client, so a card can count
  // its enabled sizes and a new kit starts on the served model.
  const [catalog, setCatalog] = React.useState<BannerKitCatalog | null>(null);
  React.useEffect(() => {
    let live = true;
    void fetchBannerKitCatalog().then((c) => {
      if (live && c) setCatalog(c);
    });
    return () => {
      live = false;
    };
  }, []);

  // `new`: the subject flow, then the draft is minted on confirm and the URL swapped to its id WITHOUT navigating
  // (navigate() would push a second history entry for the same kit and re-run the URL sync; replaceState keeps this
  // instance and the router's history state, so back/forward still work and a reload reopens this draft instead of
  // minting another). `createdRef` stops a double mint; `createGenRef` lets a stale mint (the owner left and started
  // over) tell it is no longer the current attempt.
  const [created, setCreated] = React.useState<BannerKit | null>(null);
  const [minting, setMinting] = React.useState(false);
  const createdRef = React.useRef(false);
  const createGenRef = React.useRef(0);

  // The library does not remount across /banners <-> /banners/new <-> /banners/:id (the page keys it by tab), so
  // returning to the grid resets the new-kit session; the grid is the one view with no mint in flight.
  React.useEffect(() => {
    if (view.kind !== 'library') return;
    createdRef.current = false;
    createGenRef.current += 1; // invalidate any in-flight mint
    setCreated(null);
    setMinting(false);
  }, [view.kind]);

  // `edit`: resolved from the list (there is no single-kit read). Only a SETTLED list for this tenant may declare
  // the id missing; then leave to the grid with a notice.
  React.useEffect(() => {
    if (view.kind !== 'edit' || !viewId || !loaded || loading) return;
    // The asset minted here moments ago is not missing while the list catches up: opening its stats or links sheet moves
    // the address from /new to its id before the post-mint refresh has landed it.
    if (created && created.tenant_id === String(tenantId) && created.id === viewId) return;
    if (!kits.some((k) => k.id === viewId)) {
      onNotify(t("That image banner couldn't be found."));
      navigate(routes.library(), { replace: true });
    }
  }, [view.kind, viewId, loaded, loading, kits, created, tenantId, routes, onNotify, navigate, t]);

  const goToLibrary = React.useCallback((): void => navigate(routes.library()), [navigate, routes]);
  // The composer's optimistic edits (name, document, versions, run link) land on the list entry, and on the freshly
  // minted snapshot too while the post-mint refresh has not brought the kit into the list yet.
  const patchKit = React.useCallback(
    (id: string, fields: Partial<BannerKit>): void => {
      patch(id, fields);
      setCreated((cur) => (cur && cur.id === id ? { ...cur, ...fields } : cur));
    },
    [patch],
  );
  const startNew = React.useCallback((): void => navigate(routes.create()), [navigate, routes]);
  const openKit = React.useCallback((kit: BannerKit): void => navigate(routes.edit(kit.id)), [navigate, routes]);

  // The brand's website theme (palette, CTA button, fonts) and its languages (a new kit starts in the default one)
  // prefill every new kit; fetched early so a create does not wait on them (cached per tenant, never throw).
  React.useEffect(() => {
    if (!tenantId) return;
    void fetchBrandTheme(tenantId);
    void fetchSiteLanguages(tenantId);
  }, [tenantId]);

  const mint = React.useCallback(
    (type: AdSubjectType, subject: AdSubject): void => {
      if (!tenantId || createdRef.current) return;
      createdRef.current = true;
      const gen = (createGenRef.current += 1);
      setMinting(true);
      const name = subject.name || t('Untitled image banner');
      const fail = (): void => {
        // A stale attempt must not release a newer attempt's re-entry lock (that would allow a duplicate draft).
        if (gen !== createGenRef.current) return;
        createdRef.current = false;
        setMinting(false);
        onNotify(t("That couldn't be created. Please try again."));
      };
      // The website theme (cached, bounded wait, null when the site has none) and its default language (bounded wait,
      // '' when the languages could not be read) first, then the draft.
      void Promise.all([fetchBrandTheme(tenantId), defaultLanguageWithin(tenantId)]).then(([theme, language]) => {
        if (gen !== createGenRef.current) return;
        const base = adBrandFrom(brandName, brandDomain, brandLogoUrl);
        const brand = applyBrandTheme(
          brandLogos && Object.keys(brandLogos).length > 0 ? { ...base, logos: brandLogos } : base,
          theme,
        );
        const adParams = newKitParams(type, subject, brand, catalog?.model ?? DEFAULT_KIT_MODEL, language);
        void upsertBannerKit(tenantId, {
          name,
          status: 'draft',
          image_url: subject.image_url,
          ad_params: adParams,
        }).then(
          (id) => {
            if (gen !== createGenRef.current) return;
            if (!id) {
              fail();
              return;
            }
            setMinting(false);
            setCreated({
              id,
              tenant_id: String(tenantId),
              name,
              media_type: 'banner_kit',
              image_url: subject.image_url,
              status: 'draft',
              archived: false,
              ad_params: adParams,
              ad_versions: [],
            });
            window.history.replaceState(window.history.state, '', routes.edit(id));
            refresh();
          },
          fail, // `fail` covers the request only: once the row exists, nothing may unlock a second mint
        );
      });
    },
    [tenantId, brandName, brandDomain, brandLogoUrl, brandLogos, catalog, routes, refresh, onNotify, t],
  );

  // Download ZIP: a free, long server export of the newest generated version (every enabled size at its exact
  // pixels and weight budget, plus the legal line). One at a time per kit; the owner is told it is being prepared.
  const [downloadingIds, setDownloadingIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const downloadingRef = React.useRef<Set<string>>(new Set());
  const setDownloading = React.useCallback((id: string, on: boolean): void => {
    if (on) downloadingRef.current.add(id);
    else downloadingRef.current.delete(id);
    setDownloadingIds(new Set(downloadingRef.current));
  }, []);

  const download = React.useCallback(
    (kit: BannerKit): void => {
      const version = newestLockedVersion(kit.ad_versions);
      if (!tenantId || !version || downloadingRef.current.has(kit.id)) return;
      setDownloading(kit.id, true);
      onNotify(t('Preparing your image banner ZIP…'));
      void exportBannerKit(tenantId, kit.id, { version_at: version.created_at })
        .then(async (res) => {
          if (res.ok && res.data.zip_url) {
            const label = kit.name ? `${kit.name} banner kit` : 'banner kit';
            await downloadAsset(res.data.zip_url, assetFilename(brandName, label, res.data.zip_url, 'zip'));
            return;
          }
          if (!res.ok && res.aborted) return;
          if (!res.ok && res.status === 409)
            onNotify(t('Image banner downloads are not available in this environment yet.'));
          else onNotify(t("The image banner ZIP couldn't be prepared. Please try again."));
        })
        .finally(() => setDownloading(kit.id, false));
    },
    [tenantId, brandName, onNotify, setDownloading, t],
  );

  // The kit the editor opens: the freshly minted draft for `new` (the live list entry once the post-mint refresh
  // lands it), else the one matching the :id. A cold deep link waits for the list.
  const editing = view.kind !== 'library';
  // The freshly minted draft counts only for the brand it was made for: right after a brand switch it must not stand
  // in for the (still empty) new brand's list, or its editor would render, and save, under the new brand.
  const createdHere = created && created.tenant_id === String(tenantId) ? created : null;
  const reopen: BannerKit | undefined =
    view.kind === 'new'
      ? createdHere
        ? withCreated(kits, createdHere)
        : undefined
      : viewId
        ? createdHere?.id === viewId
          ? withCreated(kits, createdHere)
          : kits.find((k) => k.id === viewId)
        : undefined;

  // The editor, keyed by the kit so opening another one starts from its own document. Closing it returns to the
  // grid (the URL is the source of truth).
  const composerNode =
    editing && reopen ? (
      <BannerKitComposer
        key={`${tenantId}:${reopen.id}`}
        tenantId={tenantId}
        kit={reopen}
        brandName={brandName}
        brandDomain={brandDomain}
        brandLogos={brandLogos}
        items={items}
        patch={patchKit}
        onClose={goToLibrary}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
      />
    ) : null;

  const archivedCount = kits.filter((k) => k.archived).length;
  const statsFor = statsForId ? kits.find((k) => k.id === statsForId && !k.archived) : undefined;

  return (
    <>
      <AdLibraryShell<BannerKit>
        kind="banner_kit"
        items={kits}
        ready={settled}
        title={t('Your image banners')}
        intro={t(
          'Every image banner you have generated, one ad in every size. Open one to edit or regenerate it, download the ZIP or archive it, or create a new one.',
        )}
        archivedTitle={t('Archived image banners ({{n}})', { n: archivedCount })}
        archivedEmptyText={t('No archived image banners.')}
        emptyText={
          // A failed read is not an empty library (a reload reads it again).
          listFailed
            ? t("Your image banners couldn't be loaded. Reload the page to try again.")
            : t('No image banners yet. Create your first one.')
        }
        loadingText={t('Loading your image banners…')}
        createLabel={t('Create new')}
        createAriaLabel={t('Create a new image banner')}
        untitledName={t('Untitled image banner')}
        archiveConfirmTitle={t('Archive this image banner?')}
        archiveConfirmBody={t(
          'It will be moved to Archived image banners, where you can restore it later or permanently delete it.',
        )}
        posterOf={(kit) => {
          const url = kitPosterUrl(kit);
          return url ? (
            <S.PosterImg key={url} {...revealOnLoad} src={url} alt="" />
          ) : (
            <S.PosterPlaceholder>
              {/* Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
              <LumioMark size={22} />
              <S.PosterEmpty text="Not generated yet" />
            </S.PosterPlaceholder>
          );
        }}
        thumbOf={kitPosterUrl}
        metaOf={(kit) =>
          catalog ? t('{{count}} sizes', { count: enabledKitSizes(catalog, kit.ad_params).length }) : t('Image banner')
        }
        stateOf={(kit) => kitCardState(kit, jobsById)}
        cardFooterSkeleton={<AssetCardStatsSkeleton />}
        cardFooterOf={(kit) => (
          <AssetCardStats
            loaded={stats.loaded}
            failed={stats.failed}
            stats={stats.byId[kit.id]}
            hint={newestLockedVersion(kit.ad_versions) ? 'get-link' : 'generate-first'}
            onOpen={() => statsSheet.open(kit.id)}
          />
        )}
        onCreate={startNew}
        onOpen={openKit}
        onDownload={download}
        canDownload={(kit) => Boolean(newestLockedVersion(kit.ad_versions))}
        downloadBusyOf={(kit) => downloadingIds.has(kit.id)}
        remove={remove}
        restore={restore}
        purge={purge}
        jobsById={jobsById}
      />

      {composerNode}

      {statsFor ? (
        <AssetStatsSheet kind="kit" creativeId={statsFor.id} name={statsFor.name} onClose={() => statsSheet.close()} />
      ) : null}

      {/* A NEW kit: what it advertises (the category, then one item of it); the draft is minted on the choice. */}
      {view.kind === 'new' && !created ? (
        <AdSubjectFlow
          items={items}
          itemsLoaded={itemsLoaded}
          promotionTitle={t('Choose the promotion this image banner advertises')}
          promotionHint={t('Pick one. Its key art and copy are the starting point for every size.')}
          busy={minting}
          onChosen={mint}
          subjectTypes={['promotion', 'game']}
          onClose={goToLibrary}
        />
      ) : null}
    </>
  );
};

export default BannerKitLibrary;
